import { sortBy } from 'lodash-es'
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { aOrganization, useAtomValue } from '~/atoms'
import { Button, SelectMultiple } from '~/components/ui'
import { useTRPC } from '~/trpc/client'

export default function MapSubmarketsFilter({
  submarkets,
  updateFilter,
  isPopover = false,
  scrollIntoView = true,
}: {
  submarkets: string[]
  updateFilter: (updates: Record<string, any>) => void
  isPopover?: boolean
  scrollIntoView?: boolean
}) {
  const trpc = useTRPC()
  const organization = useAtomValue(aOrganization)!

  const geographiesQuery = useQuery(
    trpc.geographies.submarkets.queryOptions(undefined, {
      enabled: organization.slug.includes('carnm'),
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    })
  )
  const geographies = geographiesQuery.data ?? []
  const isLoading = geographiesQuery.isLoading || geographiesQuery.isFetching

  const submarketOptions = useMemo(() => {
    const submarketOptions: { label: string; value: string }[] = []
    geographies.forEach((geo) => {
      if (
        submarketOptions.findIndex((option) => option.value === geo.name) === -1
      ) {
        submarketOptions.push({
          label: geo.label || geo.name,
          value: geo.name,
        })
      }
    })
    return sortBy(submarketOptions, 'label')
  }, [geographies])

  const selectedSubmarkets = useMemo(
    () => submarketOptions.filter(({ value }) => submarkets.includes(value)),
    [submarkets, submarketOptions]
  )

  if (geographies.length === 0 && !isLoading) {
    return null
  }

  return (
    <div className="space-y-2">
      <p className="text-sm">
        Submarkets
        {selectedSubmarkets.length > 0 && (
          <Button
            variant="link"
            size="none"
            className="ml-2"
            onClick={() => updateFilter({ submarkets: [] })}>
            (reset)
          </Button>
        )}
      </p>
      <SelectMultiple
        scrollIntoView={scrollIntoView}
        isPopover={isPopover}
        placeholder={
          isLoading && geographies.length === 0
            ? 'Loading Submarkets...'
            : 'Search Submarkets...'
        }
        options={submarketOptions}
        selected={selectedSubmarkets}
        setSelected={(selected) => {
          updateFilter({
            submarkets: selected.map(({ value }) => value),
          })
        }}
      />
    </div>
  )
}
